import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseQuery } from './getBaseQuery';
import { PostLoginRequest, PostLoginResponse, PostRegisterRequest, PostRegisterResponse, PostSendCodeRequest, PostSendCodeResponse } from './types/TLoginServices';
import { LS_KEYS } from '../store/constants';
import { updateToken, updateRefreshToken } from '../store/authSlice';
import { PostChangePasswordCodeRequest, PostChangePasswordCodeResponse, PostChangePasswordRequest, PostChangePasswordResponse } from './types/TAreaServices';

const URL_Prefix = '/auth';

export const authService = createApi({
  reducerPath: 'authService',
  baseQuery: getBaseQuery,
  tagTypes: ['Auth'],
  endpoints: (builder) => ({
    login: builder.mutation<PostLoginResponse, PostLoginRequest>({
      query: (body) => ({
        url: `${URL_Prefix}/login/`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Auth'],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.access_token && data.refresh_token) {
            localStorage.setItem(LS_KEYS.token, data.access_token);
            localStorage.setItem(LS_KEYS.refreshToken, data.refresh_token);
          }
        } catch (err) {
          // Обработка ошибок login
        }
      },
    }),
    // Изменяем на query вместо mutation и добавляем onQueryStarted
    sendCode: builder.query<PostSendCodeResponse, PostSendCodeRequest>({
      query: (body) => ({
        url: `${URL_Prefix}/send_code/`,
        method: 'POST',
        body: body,
      }),
      providesTags: ['Auth'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          
          // Проверяем и сохраняем токен если он есть
          if (data.access_token) {
            localStorage.setItem(LS_KEYS.token, data.access_token);
            
            if (data.refresh_token) {
              localStorage.setItem(LS_KEYS.refreshToken, data.refresh_token);
            }
            
            // Также можно обновить состояние в Redux
            dispatch(updateToken({ accessToken: data.access_token }));
            
            if (data.refresh_token) {
              dispatch(updateRefreshToken({ refreshToken: data.refresh_token }));
            }
          }
        } catch (error) {
          // Ошибка уже обработана в компоненте
        }
      },
    }),
    refreshToken: builder.mutation<{ accessToken: string; refreshToken: string }, { refreshToken: string }>({
      query: (body) => ({
        url: `${URL_Prefix}/refresh/`,
        method: 'POST',
        body,
      }),
    }),
    logout: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: `${URL_Prefix}/logout/`,
        method: 'POST',
      }),
    }),
    registration: builder.mutation<PostRegisterResponse, PostRegisterRequest>({
      query: (body) => ({
        url: `${URL_Prefix}/registration/`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Auth'],
    }),
    changePassword: builder.mutation<PostChangePasswordResponse, PostChangePasswordRequest>({
      query: (body) => ({
        url: `${URL_Prefix}/change_password_confirm/`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Auth'],
    }),
    changePasswordCode: builder.mutation<PostChangePasswordCodeResponse, PostChangePasswordCodeRequest>({
      query: (body) => ({
        url: `${URL_Prefix}/change_password_request/`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Auth'],
    }),
  }),
});

// Экспортируем все хуки, включая useLazySendCodeQuery
export const {
  useLoginMutation,
  useLazySendCodeQuery, // lazy query для sendCode
  useSendCodeQuery, // обычная query для sendCode
  useRefreshTokenMutation,
  useLogoutMutation,
  useRegistrationMutation,
  useChangePasswordMutation,
  useChangePasswordCodeMutation
} = authService;